import { useAccRoles, useCreateAccRole, useDeleteAccRole, useUpdateAccRole } from "@api/accRoles";
import { CreateExternalRoleRequest, UpdateExternalRoleRequest } from "@api/apiClient";
import { useRoles, useRolesWithClaims } from "@api/roles";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

interface AccRoleRowModel {
  id: string;
  externalRoleId?: string;
  name?: string;
  description?: string;
  linkedRoleName?: string;
  linkedRoleId?: string;
}

const AccRolesGrid = observer(() => {
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [rows, setRows] = useState<AccRoleRowModel[]>([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning">("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [editingRole, setEditingRole] = useState<{ id: string; name: string; roleId: string } | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const getAccRoles = useAccRoles();
  const getRoles = useRoles();
  const getRolesWithClaims = useRolesWithClaims();

  const createAccRole = useCreateAccRole({
    onSuccess: () => {
      setSnackbarMessage(i18n.t("acc-roles-tab.create.success"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setNewRoleName("");
      setSelectedRoleId("");
      getAccRoles.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || i18n.t("acc-roles-tab.create.error");
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    },
  });

  const updateAccRole = useUpdateAccRole({
    onSuccess: () => {
      setSnackbarMessage(i18n.t("acc-roles-tab.update.success"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setNewRoleName("");
      setSelectedRoleId("");
      setEditingRole(null);
      setIsEditMode(false);
      getAccRoles.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || i18n.t("acc-roles-tab.update.error");
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    },
  });

  const deleteAccRole = useDeleteAccRole({
    onSuccess: () => {
      setSnackbarMessage(i18n.t("acc-roles-tab.delete.success"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      getAccRoles.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || i18n.t("acc-roles-tab.delete.error");
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    },
  });

  useEffect(() => {
    if (getAccRoles.data?.externalRoles && getRoles.data?.roles) {
      const accRoleRows: AccRoleRowModel[] = getAccRoles.data.externalRoles.map((role, index) => {
        // Find the linked role name and ID
        let linkedRoleName = "";
        let linkedRoleId = "";
        if (role.linkedRoleIds && role.linkedRoleIds.length > 0 && getRoles.data?.roles) {
          const linkedRole = getRoles.data.roles.find((r) => r.roleId === role.linkedRoleIds![0]);
          linkedRoleName = linkedRole?.name || "";
          linkedRoleId = role.linkedRoleIds[0] || "";
        }

        return {
          id: role.externalRoleId || `role-${index}`,
          externalRoleId: role.externalRoleId,
          name: role.name,
          description: role.description,
          linkedRoleName: linkedRoleName,
          linkedRoleId: linkedRoleId,
        };
      });

      // Sort: pre-existing roles first (non-custom), then custom roles
      const sortedRows = accRoleRows.sort((a, b) => {
        const aIsCustom = a.description?.includes("- Custom") ? 1 : 0;
        const bIsCustom = b.description?.includes("- Custom") ? 1 : 0;
        // If both are same type, sort by name alphabetically
        if (aIsCustom === bIsCustom) {
          return (a.name || "").localeCompare(b.name || "");
        }
        return aIsCustom - bIsCustom;
      });

      setRows(sortedRows);
    }
  }, [getAccRoles.data, getRoles.data]);

  const handleCreateAccRole = () => {
    if (!newRoleName.trim()) {
      setSnackbarMessage(i18n.t("acc-roles-tab.create.validation.empty"));
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    if (isEditMode && editingRole) {
      // Update existing role
      const request = new UpdateExternalRoleRequest({
        externalRoleId: editingRole.id,
        name: newRoleName.trim(),
        description: `${newRoleName.trim()} - Custom`,
        roleId: selectedRoleId ? selectedRoleId : undefined,
      });
      updateAccRole.mutate(request);
    } else {
      // Create new role
      const request = new CreateExternalRoleRequest({
        name: newRoleName.trim(),
        description: `${newRoleName.trim()} - Custom`,
        roleId: selectedRoleId ? selectedRoleId : undefined,
      });
      createAccRole.mutate(request);
    }
  };

  const handleEditClick = (id: string, name: string, linkedRoleId: string) => {
    setEditingRole({ id, name, roleId: linkedRoleId });
    setNewRoleName(name);
    setSelectedRoleId(linkedRoleId || "");
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditingRole(null);
    setNewRoleName("");
    setSelectedRoleId("");
    setIsEditMode(false);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setRoleToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (roleToDelete) {
      deleteAccRole.mutate(roleToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("acc-roles-tab.grid.column.name"),
      flex: 2,
      minWidth: 200,
    },
    {
      field: "description",
      headerName: i18n.t("acc-roles-tab.grid.column.description"),
      flex: 3,
      minWidth: 300,
    },
    {
      field: "linkedRoleName",
      headerName: i18n.t("acc-roles-tab.grid.column.linkedRole"),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => {
        return params.value || "-";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("acc-roles-tab.grid.column.actions"),
      resizable: false,
      width: 100,
      getActions: (params) => {
        const isCustomRole = params.row.description?.includes("- Custom");

        if (isCustomRole) {
          return [
            <GridActionsCellItem
              key={`edit-${params.row.id}`}
              icon={
                <Tooltip title={i18n.t("acc-roles-tab.grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("acc-roles-tab.grid.edit-button.label")}
              onClick={() => handleEditClick(params.row.externalRoleId, params.row.name, params.row.linkedRoleId)}
            />,
            <GridActionsCellItem
              key={`delete-${params.row.id}`}
              icon={
                <Tooltip title={i18n.t("acc-roles-tab.grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("acc-roles-tab.grid.delete-button.label")}
              onClick={() => handleDeleteClick(params.row.externalRoleId, params.row.name)}
            />,
          ];
        }
        return [];
      },
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        gap: 2,
      }}
    >
      {/* Add New ACC Role Section */}
      <Card elevation={3} sx={{ width: "100%", "&.MuiPaper-root": { transition: "none important!" } }}>
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "1", width: "100%" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={4}>
                <TextField
                  fullWidth
                  label={i18n.t("acc-roles-tab.create.field.name")}
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateAccRole();
                    }
                  }}
                  disabled={createAccRole.isPending || updateAccRole.isPending}
                />
              </Grid>
              <Grid size={4}>
                <FormControl fullWidth>
                  <InputLabel id="role-select-label">{i18n.t("acc-roles-tab.create.field.role")}</InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    label={i18n.t("acc-roles-tab.create.field.role")}
                    disabled={createAccRole.isPending || updateAccRole.isPending}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("acc-roles-tab.create.field.role.none")}</em>
                    </MenuItem>
                    {getRolesWithClaims.data?.roles?.map((role) => (
                      <MenuItem key={role.roleId} value={role.roleId}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isEditMode && (
                <Grid size="auto">
                  <Tooltip title={i18n.t("acc-roles-tab.edit.button.cancel")}>
                    <Button variant="outlined" sx={{ whiteSpace: "nowrap" }} onClick={handleCancelEdit} disabled={createAccRole.isPending || updateAccRole.isPending}>
                      {i18n.t("acc-roles-tab.edit.button.cancel")}
                    </Button>
                  </Tooltip>
                </Grid>
              )}
              <Grid size="auto">
                <Tooltip title={isEditMode ? i18n.t("acc-roles-tab.edit.button.save") : i18n.t("acc-roles-tab.create.button.add")}>
                  <Button
                    variant="outlined"
                    sx={{ whiteSpace: "nowrap" }}
                    onClick={handleCreateAccRole}
                    disabled={createAccRole.isPending || updateAccRole.isPending || !newRoleName.trim()}
                  >
                    {isEditMode ? i18n.t("acc-roles-tab.edit.button.save") : i18n.t("acc-roles-tab.create.button.add")}
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* ACC Roles Grid */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          position: "relative",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={getAccRoles.isFetching}
          getRowId={(row) => row.id}
          sx={{
            height: height ? `${height}px` : "100%",
            width: "100%",
          }}
        />
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{i18n.t("acc-roles-tab.delete.dialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{i18n.t("acc-roles-tab.delete.dialog.message", { roleName: roleToDelete?.name })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {i18n.t("acc-roles-tab.delete.dialog.cancel")}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {i18n.t("acc-roles-tab.delete.dialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default AccRolesGrid;
